import { ReactNode } from 'react'

import classNames from '@ui/lib/utils/classNames'

export type CardItem = {
  id: number | string
  name: string
  subtitle?: string
  badge?: ReactNode
}

type CardItemProps = Pick<CardItem, 'name' | 'subtitle'> & {
  onClick?: () => void
  isSelected?: boolean
  useCheckbox?: boolean
  image?: {
    src: string
    alt: string
  }
  badge?: ReactNode
}

export const CardItem = ({ onClick, isSelected, useCheckbox, image, name, subtitle, badge = null }: CardItemProps) => {
  return (
    <div
      onClick={onClick}
      className={classNames(
        'group relative mb-2 flex items-center gap-x-6 rounded-lg border p-4 text-sm leading-6 duration-100 ease-in',
        isSelected ? 'border-primary-600 bg-primary-100' : 'border-slate-300 bg-white',
        onClick ? 'cursor-pointer hover:border-primary-200 hover:bg-primary-100' : ''
      )}>
      {useCheckbox && (
        <div className="flex h-11 w-11 items-center justify-center">
          {/* checkbox input is readOnly because the 'checked' attribute is manipulated on the parent div onClick */}
          <input className="text-primary-500" type="checkbox" checked={isSelected} readOnly />
        </div>
      )}
      <div className="h-12 w-12">{image && <img src={image.src} alt={image.alt} />}</div>
      <div className="flex-auto">
        <p className="block font-semibold">{name}</p>
        <span className="absolute inset-0" />
        {subtitle && <p className="mt-1 text-gray-600">{subtitle}</p>}
      </div>
      {badge}
    </div>
  )
}

type CardStackProps<T extends CardItem> = {
  items: T[]
  onSelect: (item: T) => void
  isSelected: (item: T) => boolean
  getImage: (item: T) => { src: string; alt: string }
  useCheckbox?: boolean
}

export const CardStack = <T extends CardItem>({
  items,
  onSelect,
  isSelected,
  getImage,
  useCheckbox,
}: CardStackProps<T>) => {
  return (
    <div>
      {items.map((item) => {
        const { src, alt } = getImage(item)

        const selected = isSelected(item)

        return (
          <CardItem
            key={item.id}
            onClick={() => onSelect(item)}
            isSelected={selected}
            useCheckbox={useCheckbox}
            image={{
              src,
              alt,
            }}
            name={item.name}
            subtitle={item.subtitle}
            badge={item.badge}
          />
        )
      })}
    </div>
  )
}
